import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useLocation, useNavigate } from "react-router-dom";
import { Customer } from "../../../../../models/Customer/Customer";
import { SortType } from "../../../../../models/SortType";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

interface CustomerTableProp {
  maxHeight: number | string;
  customers: Customer[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  onDelete: (customerId: number) => void;
  onActivate?: (customerId: number) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange?: (limit: number) => void;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
}

interface ColumnConfig {
  id: string;
  label: string;
  sortable?: boolean;
  minWidth?: number | string;
  align?: "left" | "center" | "right";
}

const CustomerTable: React.FC<CustomerTableProp> = ({
  maxHeight,
  customers,
  page_no,
  limit,
  total_pages,
  total_count,
  sortBy,
  onPageChange,
  onDelete,
  onActivate,
  sortType,
  onSortChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const openCustomer = (customerId: number) => {
    navigate(`/customers/add-edit-customer?customer-id=${customerId}`, {
      state: { listingSearch: location.search },
    });
  };
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const c = theme.palette.customColors;
  const { userStore } = useStores();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);

  const isOpen = Boolean(anchorEl);

  const canManageCustomer =
    userStore.hasPermission(EmployeeRoles.CREATE_CUSTOMER) ||
    userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER);

  const rowClickable = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_CUSTOMER) ||
      userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER) ||
      userStore.hasPermission(EmployeeRoles.CREATE_CUSTOMER)
    );
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    customer: Customer,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentCustomer(null);
  };

  const handleEdit = () => {
    if (currentCustomer?.id) {
      openCustomer(Number(currentCustomer.id));
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (currentCustomer?.id) {
      onDelete(Number(currentCustomer.id));
    }
    handleMenuClose();
  };

  const handleActivate = () => {
    if (currentCustomer?.id && onActivate) {
      onActivate(Number(currentCustomer.id));
    }
    handleMenuClose();
  };

  const handleSort = (colId: string) => {
    const property = colId === "configModifiedDate" ? "modifiedDate" : colId;
    if (sortBy === property) {
      onSortChange(
        property,
        sortType === SortType.ASC ? SortType.DESC : SortType.ASC,
      );
    } else {
      onSortChange(property, SortType.DESC);
    }
  };

  const renderSortIcon = (colId: string) => {
    const property = colId === "configModifiedDate" ? "modifiedDate" : colId;
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const formatDateTime = (dateVal?: Date | string | null) => {
    if (!dateVal) return "—";
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const renderBoolean = (val: any) => {
    const isTrue = val === true || val === 1 || val === "1" || val === "true";
    return (
      <CustomTypography
        variant="body2"
        fontWeight={500}
        color={isTrue ? colors.success : colors.error}
      >
        {isTrue ? "True" : "False"}
      </CustomTypography>
    );
  };

  const bodyCellSx = {
    py: 2,
    px: 2,
    borderBottom: `1px solid ${c.lightGreyColor}`,
    whiteSpace: "nowrap" as const,
  };

  const stickyShadow = `-4px 0 8px -2px ${c.shadowLightBorder}`;

  const columns: ColumnConfig[] = [
    { id: "id", label: "Id", sortable: true, minWidth: 80 },
    { id: "name", label: "Name", sortable: true, minWidth: 160 },
    { id: "zoho_id", label: "Zoho ID", sortable: false, minWidth: 100 },
    { id: "createdDate", label: "Created Date", sortable: true, minWidth: 180 },
    {
      id: "modifiedDate",
      label: "Modified Date",
      sortable: true,
      minWidth: 180,
    },
    {
      id: "configModifiedDate",
      label: "Config Modified Date",
      sortable: true,
      minWidth: 190,
    },
    {
      id: "send_redemption_email",
      label: "Email Redemption",
      sortable: false,
      minWidth: 160,
    },
    {
      id: "email",
      label: "Redemption Email From",
      sortable: false,
      minWidth: 220,
    },
    {
      id: "from_email_name",
      label: "Customer Redemption Email From",
      sortable: false,
      minWidth: 230,
    },
    { id: "goc", label: "GOC", sortable: false, minWidth: 90 },
    { id: "campaigns", label: "Campaigns", sortable: false, minWidth: 110 },
    { id: "swagstore", label: "Swagstore", sortable: false, minWidth: 110 },
    { id: "inventory", label: "Inventory", sortable: false, minWidth: 110 },
    { id: "active", label: "Active", sortable: false, minWidth: 90 },
    ...(canManageCustomer
      ? [
          {
            id: "actions",
            label: "Actions",
            sortable: false,
            minWidth: 80,
            align: "center" as const,
          },
        ]
      : []),
  ];

  const fromRange = total_count === 0 ? 0 : (page_no - 1) * limit + 1;
  const toRange = Math.min(page_no * limit, total_count);

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer
        sx={{
          maxHeight,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 1800 }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const isActions = col.id === "actions";
                const isSorted =
                  (col.id === "configModifiedDate" &&
                    sortBy === "modifiedDate") ||
                  sortBy === col.id;

                return (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    onClick={
                      col.sortable ? () => handleSort(col.id) : undefined
                    }
                    sx={{
                      py: 1.75,
                      px: 2,
                      minWidth: col.minWidth,
                      backgroundColor: colors.surface,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: colors.label,
                      whiteSpace: "nowrap",
                      cursor: col.sortable ? "pointer" : "default",
                      userSelect: col.sortable ? "none" : "auto",
                      ...(isActions
                        ? {
                            position: "sticky",
                            right: 0,
                            top: 0,
                            zIndex: 13,
                            boxShadow: stickyShadow,
                            borderLeft: `1px solid ${c.lightGreyColor}`,
                          }
                        : {}),
                      "&:hover": col.sortable
                        ? { color: colors.accentPurple }
                        : undefined,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <span>{col.label}</span>
                      {col.sortable && (
                        <CustomTypography
                          variant="caption"
                          fontWeight={500}
                          color={isSorted ? colors.title : c.mediumGray}
                          style={{ marginLeft: 4 }}
                        >
                          {renderSortIcon(col.id)}
                        </CustomTypography>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer: Customer) => {
              const isRowClickable = rowClickable() && !customer.is_deleted;

              return (
                <TableRow
                  key={customer.id}
                  hover
                  onClick={() => {
                    if (isRowClickable) {
                      openCustomer(Number(customer.id));
                    }
                  }}
                  sx={{
                    cursor: isRowClickable ? "pointer" : "default",
                    backgroundColor: colors.surface,
                    "&:hover": {
                      backgroundColor: `${c.lightGrayBackground} !important`,
                      "& .sticky-action-cell": {
                        backgroundColor: `${c.lightGrayBackground} !important`,
                      },
                    },
                    borderBottom: `1px solid ${c.lightGreyColor}`,
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={700}
                      color={colors.title}
                    >
                      {customer.id}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={700}
                      color={colors.title}
                    >
                      {customer.name || "—"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.mediumGray}>
                      {customer.zoho_id || "NA"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {formatDateTime(customer.created_date)}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {formatDateTime(customer.modified_date)}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {formatDateTime(customer?.config?.modified_date)}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {renderBoolean(
                      customer?.config?.send_redemption_email ??
                        customer?.config?.coupon_redemption_enabled,
                    )}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography
                      variant="body2"
                      color={
                        customer?.config?.email ? c.navyText : c.mediumGray
                      }
                    >
                      {customer?.config?.email || "rewards@redeem.swageazy.com"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography
                      variant="body2"
                      color={
                        customer?.config?.from_email_name
                          ? c.navyText
                          : c.mediumGray
                      }
                    >
                      {customer?.config?.from_email_name || "—"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {renderBoolean(customer?.config?.gift_of_choice_enabled)}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {renderBoolean(customer?.config?.campaigns_enabled)}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {renderBoolean(customer?.config?.swagstore_enabled)}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {renderBoolean(customer?.config?.inventory_enabled)}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {renderBoolean(!customer.is_deleted)}
                  </TableCell>

                  {canManageCustomer && (
                    <TableCell
                      className="sticky-action-cell"
                      sx={{
                        ...bodyCellSx,
                        px: 1,
                        textAlign: "center",
                        position: "sticky",
                        right: 0,
                        zIndex: 9,
                        backgroundColor: colors.surface,
                        boxShadow: stickyShadow,
                        borderLeft: `1px solid ${c.lightGreyColor}`,
                        minWidth: 80,
                        width: 80,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, customer)}
                        sx={{ color: colors.subtitle }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            minWidth: 120,
            border: `1px solid ${colors.border}`,
            boxShadow: `0 4px 12px ${c.shadowLightBorder}`,
            py: 0.5,
            backgroundColor: colors.surface,
          },
        }}
      >
        {!currentCustomer?.is_deleted && (
          <MenuItem
            onClick={() => handleEdit()}
            sx={{
              fontSize: "0.8rem",
              py: 1,
              px: 2,
              color: c.navyText,
              "&:hover": {
                backgroundColor: colors.headerBg,
                color: colors.accentPurple,
              },
            }}
          >
            View/Edit
          </MenuItem>
        )}
        {currentCustomer?.is_deleted === 0 && (
          <MenuItem
            onClick={() => handleDelete()}
            sx={{
              fontSize: "0.8rem",
              py: 1,
              px: 2,
              color: colors.error,
              "&:hover": {
                backgroundColor: `${colors.error}14`,
              },
            }}
          >
            Delete
          </MenuItem>
        )}
        {canManageCustomer && !!currentCustomer?.is_deleted && onActivate && (
          <MenuItem
            onClick={() => handleActivate()}
            sx={{
              fontSize: "0.8rem",
              py: 1,
              px: 2,
              color: colors.success,
              "&:hover": {
                backgroundColor: c.successGreenLightBackground,
              },
            }}
          >
            Activate Customer
          </MenuItem>
        )}
      </Menu>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          px: 3,
          py: 1.75,
          gap: 1.5,
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.surface,
        }}
      >
        <CustomTypography
          variant="caption"
          color={c.mediumGray}
          style={{ userSelect: "none" }}
        >
          ← Scroll horizontally →
        </CustomTypography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 2, sm: 3 },
            ml: { xs: 0, sm: "auto" },
            flexWrap: "wrap",
          }}
        >
          <CustomTypography
            variant="caption"
            color={c.mediumGray}
            fontWeight={600}
            style={{ letterSpacing: "0.05em", textTransform: "uppercase" }}
          >
            SHOWING {customers.length} OF {total_count} ROWS
          </CustomTypography>

          <CustomTypography
            variant="caption"
            color={c.navyText}
            fontWeight={500}
          >
            {fromRange} - {toRange} of {total_count}
          </CustomTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              disabled={page_no <= 1}
              onClick={(e) => onPageChange(e, page_no - 1)}
              sx={{
                color: c.mutedSlateText,
                p: 0.5,
                "&.Mui-disabled": { color: c.switchTrackGray },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={
                page_no >= total_pages ||
                (limit > 0 && page_no * limit >= total_count)
              }
              onClick={(e) => onPageChange(e, page_no + 1)}
              sx={{
                color: c.mutedSlateText,
                p: 0.5,
                "&.Mui-disabled": { color: c.switchTrackGray },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerTable;
